import React, { useCallback, useMemo, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import { useSelector } from "react-redux";

import useGetAllChatBotSessions from "@fartherfinance/frontend/api/Chatbot/hooks/useGetAllChatBotSessions";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import useApplicationMode from "../../../hooks/useApplicationMode";
import {
  CHATBOT_EVENTS,
  DEFAULT_RIGHT_POSITION,
  DRAWER_MARGIN,
  EXTRA_LARGE_DRAWER_MARGIN,
  EXTRA_LARGE_DRAWER_WIDTH,
  LARGE_DRAWER_WIDTH,
  STANDARD_DRAWER_WIDTH,
} from "../constants";
import { ChatProvider } from "../reducer/Context";
import { ChatAnalytics } from "../utils/analyticsContext";
import { trackChatbotEvent } from "../utils/trackChatbotEvent";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { State } from "@src/store";

import ChatBotWindow from "./ChatBotWindow";

import styles from "../style/FloatingChatButton.module.css";

const FloatingChatButton: React.FC = () => {
  const { color } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { enabledChatBotFeature } = useFlags();

  const advisorAuth = useAdvisorRequestAuth();

  // Pre-fetch all chat bot sessions to determine if there are existing sessions
  const { data: chatBotMessagesResponse } = useGetAllChatBotSessions(
    {
      page: 1,
      pageSize: 20,
    },
    advisorAuth
  );

  const isLoggedIn = useSelector(
    (state: State) => state.main_Reducer.isLoggedIn
  );
  const drawerState = useSelector(
    (state: State) => state.main_Reducer.mui_drawer
  );

  const { isAdvisorMode, isImpersonationMode } = useApplicationMode();

  const rightPosition = useMemo(() => {
    if (!drawerState.isOpen) {
      return DEFAULT_RIGHT_POSITION;
    }

    const width = drawerState.width ?? 0;

    if (width >= 1004) {
      return EXTRA_LARGE_DRAWER_WIDTH + EXTRA_LARGE_DRAWER_MARGIN;
    }

    if (width >= 790) {
      return LARGE_DRAWER_WIDTH + DRAWER_MARGIN;
    }

    return STANDARD_DRAWER_WIDTH + DRAWER_MARGIN;
  }, [drawerState.isOpen, drawerState.width]);

  const containerStyle = useMemo(
    () => ({
      right: `${rightPosition}px`,
    }),
    [rightPosition]
  );

  const toggleChat = useCallback(() => {
    const willOpen = !isOpen;

    trackChatbotEvent(willOpen ? CHATBOT_EVENTS.OPEN : CHATBOT_EVENTS.CLOSE);

    if (isOpen) {
      const analytics = ChatAnalytics.getAnalyticsData();
      if (analytics.sessionId) {
        trackChatbotEvent(CHATBOT_EVENTS.UNUSED_SESSION, {
          sessionId: analytics.sessionId,
        });
      }
    }

    setIsOpen(willOpen);

    if (isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  const toggleExpand = useCallback(() => {
    const willExpand = !isExpanded;

    trackChatbotEvent(
      willExpand ? CHATBOT_EVENTS.EXPAND : CHATBOT_EVENTS.COLLAPSE
    );

    setIsExpanded(willExpand);
  }, [isExpanded]);

  if (!isLoggedIn || !(isAdvisorMode || isImpersonationMode)) {
    return null;
  }

  return enabledChatBotFeature ? (
    <>
      <div className={styles.buttonContainer} style={containerStyle}>
        <div
          onClick={toggleChat}
          className={styles.button}
          aria-label="Open chat"
          role="button"
          tabIndex={0}
          data-testid="floating-chat-button"
        >
          {isOpen ? (
            <svg
              className={`${styles.icon} ${styles.iconOpen}`}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                fill={color.$iconInverse}
              />
            </svg>
          ) : (
            <svg
              className={`${styles.icon} ${styles.iconClosed}`}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                fill={color.$iconInverse}
              />
            </svg>
          )}
        </div>
      </div>

      <ChatProvider>
        {isOpen && (
          <ChatBotWindow
            onClose={toggleChat}
            rightPosition={rightPosition}
            isExpanded={isExpanded}
            onToggleExpand={toggleExpand}
            hasSessions={
              Boolean(chatBotMessagesResponse?.data) &&
              (chatBotMessagesResponse?.data ?? []).length > 0
            }
          />
        )}
      </ChatProvider>
    </>
  ) : null;
};

export default FloatingChatButton;
